import { type RouteDefinition } from '@epihub/core/src/routes';
import { safely } from '@epihub/core/src/fn/safely';
import { type ParserMap } from '../parser';

export function parseRoute(route: RouteDefinition, fullURL: string) {
  const url = new URL(fullURL);

  const unparsedSearch = Object.fromEntries(url.searchParams);
  const search = (() => {
    const result = {};
    for (const [key, value] of Object.entries(unparsedSearch)) {
      const parsed = safely(
        () => route.parseSearch[key]?.parse(value as string),
        () => undefined,
      ) ?? value;

      result[key] = parsed;
    }

    return result;
  })();

  const path = parsePathParams(
    route.path,
    route.parsePath,
    fullURL,
  );

  return {
    hash: { [url.hash]: url.hash },
    search,
    path,
  };
}

function parsePathParams(
  fullTemplate: string,
  parserMap: ParserMap<any>,
  fullURL: string,
) {
  const url = new URL(fullURL);
  const mapped = matchPath(fullTemplate, url.pathname);
  if (!mapped) {
    return {};
  }

  const parsedParams = Object.keys(mapped.params)
    .reduce<any>((acc, k) => ({
      ...acc,
      ...(parserMap[k] ? { [k]: parserMap[k].parse(mapped.params[k]) } : {}),
    }), {});

  return parsedParams;
}

/**
 * Performs pattern matching on a URL pathname and returns information about
 * the match.
 *
 * @see https://reactrouter.com/docs/en/v6/utils/match-path
 * @see https://github.com/remix-run/react-router/blob/892238bee052177e78012167c088290b06400206/packages/router/utils.ts
 */
function matchPath(inPattern: string, pathname: string) {
  let pattern: any = inPattern;
  if (typeof pattern === 'string') {
    pattern = {
      path: pattern,
      caseSensitive: false,
      end: false,
    };
  }

  const [matcher, paramNames] = compilePath(
    pattern.path,
    pattern.caseSensitive,
    pattern.end,
  );

  const match = pathname.match(matcher);
  if (!match) {
    return null;
  }

  const matchedPathname = match[0];
  let pathnameBase = matchedPathname.replace(/(.)\/+$/, '$1');
  const captureGroups = match.slice(1);
  const params = paramNames.reduce(
    (memo, paramName, index) => {
      // We need to compute the pathnameBase here using the raw splat value
      // instead of using params["*"] later because it will be decoded then
      if (paramName === '*') {
        const splatValue = captureGroups[index] || '';
        pathnameBase = matchedPathname
          .slice(0, matchedPathname.length - splatValue.length)
          .replace(/(.)\/+$/, '$1');
      }

      memo[paramName] = decodeURIComponent(captureGroups[index] || '');
      return memo;
    },
    {},
  );

  return {
    params,
    pathname: matchedPathname,
    pathnameBase,
    pattern,
  };
}

function compilePath(
  path: string,
  caseSensitive = false,
  end = true,
): [RegExp, string[]] {
  const paramNames: string[] = [];
  let regexpSource = '^'
    + path
      .replace(/\/*\*?$/, '') // Ignore trailing / and /*, we'll handle it below
      .replace(/^\/*/, '/') // Make sure it has a leading /
      // .replace(/[\\.*+^$?{}|()[\]]/g, "\\$&") // Escape special regex chars
      .replace(/:(\w+)/g, (_: string, paramName: string) => {
        paramNames.push(paramName);
        return '([^\\/]+)';
      });

  if (path.endsWith('*')) {
    paramNames.push('*');
    regexpSource += path === '*' || path === '/*'
      ? '(.*)$' // Already matched the initial /, just match the rest
      : '(?:\\/(.+)|\\/*)$'; // Don't include the / in params["*"]
  } else if (end) {
    // When matching to the end, ignore trailing slashes
    regexpSource += '\\/*$';
  } else if (path !== '' && path !== '/') {
    // If our path is non-empty and contains anything beyond an initial slash,
    // then we have _some_ form of path in our regex so we should expect to
    // match only if we find the end of this path segment.  Look for an optional
    // non-captured trailing slash (to match a portion of the URL) or the end
    // of the path (if we've matched to the end).  We used to do this with a
    // word boundary but that gives false positives on routes like
    // /user-preferences since `-` counts as a word boundary.
    regexpSource += '(?:(?=\\/|$))';
  } else {
    // Nothing to match for "" or "/"
  }

  const matcher = new RegExp(regexpSource, caseSensitive ? undefined : 'i');

  return [matcher, paramNames];
}
